import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Package, CheckCircle, XCircle, Clock, CreditCard, MessageSquare, RefreshCcw } from 'lucide-react';
import { Order, InternalNoteEntry, RefundEntry } from '../../types';
import { format } from 'date-fns';
import { orderApi, paymentApi } from '../../services/api';
import { useOrders } from '../../context/OrderContext';

const AdminOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { orders, updateOrder } = useOrders();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusUpdate, setStatusUpdate] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [internalStatusUpdate, setInternalStatusUpdate] = useState<string>('');
  const [internalNotes, setInternalNotes] = useState<string>('');
  const [fourOverOrderNumber, setFourOverOrderNumber] = useState<string>('');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [refundReason, setRefundReason] = useState<string>('');
  const [refundLoading, setRefundLoading] = useState(false);

  useEffect(() => {
    if (id) {
      const foundOrder = orders.find(o => o.id === id);
      if (foundOrder) {
        setOrder(foundOrder);
        setStatusUpdate(foundOrder.status);
        setAdminNotes(foundOrder.notes || '');
        setInternalStatusUpdate(foundOrder.internalStatus || 'new');
        setFourOverOrderNumber(foundOrder.fourOverOrderNumber || '');
      }
      setLoading(false);
    }
  }, [id, orders]);

  const handleUpdateOrder = async () => {
    if (order) {
      try {
        // Check if customer-facing status changed
        const customerStatusChanged = statusUpdate !== order.status;
        
        // First update customer-facing fields
        await updateOrder(order.id, {
          status: statusUpdate as Order['status'],
          notes: adminNotes,
          fourOverOrderNumber: fourOverOrderNumber || undefined,
        });
        
        // Build audit note for changes
        let auditNote = '';
        
        // Add customer status change to audit
        if (customerStatusChanged) {
          auditNote = `Customer status changed from "${order.status}" to "${statusUpdate}"`;
        }
        
        // Check if internal status changed
        const internalStatusChanged = internalStatusUpdate !== order.internalStatus;
        
        // Add internal notes if provided or if status changed
        if (internalNotes.trim()) {
          if (auditNote) auditNote += '. ';
          auditNote += internalNotes;
        } else if (internalStatusChanged && !customerStatusChanged) {
          // Only add automatic note if no manual note was provided and only internal status changed
          auditNote = `Internal status changed from "${order.internalStatus || 'new'}" to "${internalStatusUpdate}"`;
        }
        
        // Update internal status/notes if there's a change or note
        if (internalStatusChanged || auditNote) {
          const updatedOrder = await orderApi.updateInternalStatus(
            order.id, 
            internalStatusUpdate as Order['internalStatus'],
            auditNote
          );
          
          // Update the order in the context with the response from the API
          await updateOrder(order.id, updatedOrder);
        }
        
        // Clear the internal notes field after successful update
        setInternalNotes('');
        
        // Get the updated order
        const refreshedOrder = orders.find(o => o.id === order.id);
        if (refreshedOrder) {
          setOrder(refreshedOrder);
        }
      } catch (error) {
        console.error('Failed to update order:', error);
        alert('Failed to update order. Please try again.');
      }
    }
  };

  const handleRefund = async () => {
    if (!order || !order.paymentId) {
      alert('No payment ID found for this order');
      return;
    }

    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid refund amount');
      return;
    }

    const totalPaid = order.totalPrice || 0;
    const totalRefunded = order.totalRefunded || 0;
    const remainingAmount = totalPaid - totalRefunded;

    if (amount > remainingAmount) {
      alert(`Refund amount cannot exceed ${remainingAmount.toFixed(2)} (remaining paid amount)`);
      return;
    }

    setRefundLoading(true);
    try {
      const result = await paymentApi.refund({
        orderId: order.id,
        amount: amount,
        reason: refundReason || 'Refund requested by admin'
      });

      // Update the order in the context
      await updateOrder(order.id, result.order);
      
      // Update local state
      setOrder(result.order);
      
      // Clear form and close modal
      setRefundAmount('');
      setRefundReason('');
      setShowRefundModal(false);
      
      alert(`Refund of $${amount.toFixed(2)} processed successfully`);
    } catch (error) {
      console.error('Failed to process refund:', error);
      alert('Failed to process refund. Please try again.');
    } finally {
      setRefundLoading(false);
    }
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending_payment':
        return 'bg-orange-100 text-orange-800';
      case 'paid':
        return 'bg-indigo-100 text-indigo-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'pending_payment':
        return <CreditCard className="h-4 w-4" />;
      case 'paid':
        return <CreditCard className="h-4 w-4" />;
      case 'processing':
        return <Package className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getInternalStatusColor = (status?: Order['internalStatus']) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status) {
      case 'new':
        return 'bg-purple-100 text-purple-800';
      case 'design_review':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'printing':
        return 'bg-indigo-100 text-indigo-800';
      case 'quality_check':
        return 'bg-yellow-100 text-yellow-800';
      case 'packaging':
        return 'bg-orange-100 text-orange-800';
      case 'ready_for_pickup':
        return 'bg-teal-100 text-teal-800';
      case 'shipped':
        return 'bg-green-100 text-green-800';
      case 'on_hold':
        return 'bg-red-100 text-red-800';
      case 'issue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600">Order not found</p>
          <Link to="/admin/orders" className="mt-4 inline-flex items-center text-blue-600 hover:underline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/admin/orders" className="inline-flex items-center text-blue-600 hover:underline mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Order Details: {order.id}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Side */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Order ID</p>
                <p className="font-medium">{order.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Customer Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                  {getStatusIcon(order.status)}
                  <span className="ml-1 capitalize">{order.status.replace('_', ' ')}</span>
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Internal Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getInternalStatusColor(order.internalStatus)}`}>
                  <span className="capitalize">{(order.internalStatus || 'new').replace('_', ' ')}</span>
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">4over Order #</p>
                <p className="font-medium">{order.fourOverOrderNumber || 'Not assigned'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="font-medium text-lg">${(order.totalPrice || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Order Date</p>
                <p className="font-medium">{order.createdAt ? format(new Date(order.createdAt), 'MMM d, yyyy h:mm a') : 'N/A'}</p>
              </div>
            </div>
            {order.notes && (
              <div className="mt-4 p-3 bg-blue-50 rounded">
                <p className="text-sm font-medium text-gray-700">Customer Notes:</p>
                <p className="text-sm text-gray-600">{order.notes}</p>
              </div>
            )}
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{order.customerInfo?.firstName || 'Unknown'} {order.customerInfo?.lastName || ''}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{order.customerEmail || order.customerInfo?.email || 'No email'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium">{order.customerPhone || order.customerInfo?.phone || 'No phone'}</p>
              </div>
              {order.customerInfo?.company && (
                <div>
                  <p className="text-sm text-gray-600">Company</p>
                  <p className="font-medium">{order.customerInfo.company}</p>
                </div>
              )}
              <div className="col-span-2">
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-medium">
                  {order.customerInfo?.address || 'No address'}<br />
                  {order.customerInfo?.city || 'Unknown'}, {order.customerInfo?.state || 'Unknown'} {order.customerInfo?.zipCode || 'Unknown'}
                </p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Items</h2>
            <div className="space-y-4">
              {(order.items || []).map((item) => (
                <div key={item.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-grow">
                      <h4 className="font-medium text-gray-900">{item.product.name}</h4>
                      <div className="text-sm text-gray-600 mt-1">
                        <p>Size: {item.width}" × {item.height}" ({((item.width * item.height) / 144).toFixed(2)} sq ft)</p>
                        <p>Quantity: {item.quantity}</p>
                        {item.material && (
                          <p>Material: {item.product.materials?.find(m => m.id === item.material)?.name || item.material}</p>
                        )}
                        {item.finishing && (
                          <p>Finishing: {item.product.finishingOptions?.find(f => f.id === item.finishing)?.name || item.finishing}</p>
                        )}
                        {item.colorSpec && (
                          <p>Color Spec: {item.product.colorSpecs?.find(c => c.id === item.colorSpec)?.name || item.colorSpec}</p>
                        )}
                        {item.customText && <p>Notes: {item.customText}</p>}
                        {item.uploadedFile && <p className="text-green-600">✓ Artwork uploaded</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                      <p className="text-sm text-gray-600">${item.price.toFixed(2)} each</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Information */}
          {(order.paymentId || order.paymentStatus || order.stripeSessionId) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Information</h2>
              <div className="grid grid-cols-2 gap-4">
                {order.paymentStatus && (
                  <div>
                    <p className="text-sm text-gray-600">Payment Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      order.paymentStatus === 'completed' ? 'bg-green-100 text-green-800' :
                      order.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                      order.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      order.paymentStatus === 'failed' ? 'bg-red-100 text-red-800' :
                      order.paymentStatus === 'refunded' ? 'bg-purple-100 text-purple-800' :
                      order.paymentStatus === 'partially_refunded' ? 'bg-indigo-100 text-indigo-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {order.paymentStatus.replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  </div>
                )}
                {order.paymentMethod && (
                  <div>
                    <p className="text-sm text-gray-600">Payment Method</p>
                    <p className="font-medium">{order.paymentMethod}</p>
                  </div>
                )}
                {order.paidAt && (
                  <div>
                    <p className="text-sm text-gray-600">Paid At</p>
                    <p className="font-medium">{format(new Date(order.paidAt), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                )}
                {order.stripeSessionId && (
                  <div>
                    <p className="text-sm text-gray-600">Stripe Session ID</p>
                    <p className="font-mono text-xs bg-gray-100 p-1 rounded">{order.stripeSessionId}</p>
                  </div>
                )}
                {order.paymentId && (
                  <div>
                    <p className="text-sm text-gray-600">Payment ID</p>
                    <p className="font-mono text-xs bg-gray-100 p-1 rounded">{order.paymentId}</p>
                  </div>
                )}
              </div>
              
              {/* Refund button */}
              {(order.paymentStatus === 'completed' || order.paymentStatus === 'partially_refunded') && order.paymentId && (
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => setShowRefundModal(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    Process Refund
                  </button>
                </div>
              )}
              
              {/* Refund history */}
              {order.refunds && order.refunds.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="font-medium text-gray-700 mb-2">Refund History</h3>
                  <div className="space-y-2">
                    {order.refunds.map((refund: RefundEntry) => (
                      <div key={refund.id} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium">${refund.amount.toFixed(2)}</p>
                            <p className="text-xs text-gray-600">{refund.reason || 'No reason provided'}</p>
                            <p className="text-xs text-gray-500">
                              {format(new Date(refund.createdAt), 'MMM d, yyyy h:mm a')}
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            refund.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                            refund.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {refund.status}
                          </span>
                        </div>
                      </div>
                    ))}
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-sm font-medium">
                        Total Refunded: ${(order.totalRefunded || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Shipping Information */}
          {order.shippingAddress && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Shipping Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Shipping Address</h3>
                  <p className="text-sm">
                    {order.shippingAddress.line1}<br />
                    {order.shippingAddress.line2 && <>{order.shippingAddress.line2}<br /></>}
                    {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postal_code}<br />
                    {order.shippingAddress.country}
                  </p>
                </div>
                {order.shipping && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Shipping Costs</h3>
                    <div className="text-sm space-y-1">
                      <p>Subtotal: ${(order.shipping.amount_subtotal / 100).toFixed(2)}</p>
                      <p>Tax: ${(order.shipping.amount_tax / 100).toFixed(2)}</p>
                      <p className="font-medium">Total: ${(order.shipping.amount_total / 100).toFixed(2)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Right Side */}
        <div className="space-y-6">
          {/* Order Management */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Management</h2>
            
            {/* Customer-facing status */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-800 mb-3">Customer-facing Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Status
                  </label>
                  <select
                    value={statusUpdate}
                    onChange={(e) => setStatusUpdate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="pending_payment">Pending Payment</option>
                    <option value="paid">Paid</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Notes
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notes visible to customer..."
                  />
                </div>
              </div>
            </div>
            
            {/* Internal status */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-800 mb-3">Internal Tracking</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Internal Status
                  </label>
                  <select
                    value={internalStatusUpdate}
                    onChange={(e) => setInternalStatusUpdate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="new">New</option>
                    <option value="design_review">Design Review</option>
                    <option value="approved">Approved</option>
                    <option value="printing">Printing</option>
                    <option value="quality_check">Quality Check</option>
                    <option value="packaging">Packaging</option>
                    <option value="ready_for_pickup">Ready for Pickup</option>
                    <option value="shipped">Shipped</option>
                    <option value="on_hold">On Hold</option>
                    <option value="issue">Issue</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    4over Order Number
                  </label>
                  <input
                    type="text"
                    value={fourOverOrderNumber}
                    onChange={(e) => setFourOverOrderNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter 4over order tracking number..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add Internal Note
                  </label>
                  <textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add a new internal note (not visible to customer)..."
                  />
                </div>
              </div>
            </div>
            
            <button
              onClick={handleUpdateOrder}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Update Order
            </button>
          </div>

          {/* Internal Notes Log */}
          {order.internalNotesLog && order.internalNotesLog.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Internal Notes History
              </h2>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {order.internalNotesLog
                  .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                  .map((entry: InternalNoteEntry) => (
                    <div key={entry.id} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-sm text-gray-700">{entry.userName}</span>
                        <span className="text-xs text-gray-500">
                          {format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                      {entry.statusChange && (
                        <div className="text-xs text-gray-600 mb-1">
                          Status: <span className="font-medium">{entry.statusChange.from}</span> → <span className="font-medium">{entry.statusChange.to}</span>
                        </div>
                      )}
                      <p className="text-sm text-gray-600">{entry.note}</p>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Refund Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Process Refund</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refund Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    max={(order?.totalPrice || 0) - (order?.totalRefunded || 0)}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Maximum refundable: ${((order?.totalPrice || 0) - (order?.totalRefunded || 0)).toFixed(2)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Refund
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Provide a reason for the refund..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleRefund}
                  disabled={refundLoading || !refundAmount}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                >
                  {refundLoading ? 'Processing...' : 'Process Refund'}
                </button>
                <button
                  onClick={() => {
                    setShowRefundModal(false);
                    setRefundAmount('');
                    setRefundReason('');
                  }}
                  disabled={refundLoading}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrderDetail;